import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Bouton, Invisible } from '../ui';

/** Ce que le parent doit savoir avant d'ouvrir un dossier, dans l'ordre où ça le concerne. */
const ETAPES: { titre: string; corps: ReactNode }[] = [
  {
    titre: 'Bienvenue sur Repère',
    corps: (
      <>
        <p>
          Repère réunit au même endroit ce qui doit circuler entre deux parents séparés :
          le calendrier de garde, les échanges, les dépenses partagées et les documents de
          vos enfants.
        </p>
        <p>
          Tout part d'un <strong>dossier</strong> : un par famille, que vous partagerez
          avec l'autre parent.
        </p>
      </>
    ),
  },
  {
    titre: 'Les deux parents, à égalité',
    corps: (
      <>
        <p>
          Aucun des deux n'administre le dossier. Mêmes droits, mêmes informations, mêmes
          écrans. Personne ne peut en exclure l'autre ni décider seul de ce qu'il contient.
        </p>
        <p>
          {/* guardrail-ok: la phrase annonce l'absence d'accusé de lecture, elle n'en pose pas. */}
          Il n'y a ni accusé de lecture, ni statut « en ligne ». Ce que vous écrivez est
          horodaté à l'envoi — et rien n'indique à l'autre parent quand vous l'avez lu.
        </p>
      </>
    ),
  },
  {
    titre: 'Rien ne s’efface',
    corps: (
      <>
        <p>
          Chaque élément partagé est horodaté par nos serveurs, puis scellé : il ne peut
          plus être modifié ni supprimé — ni par vous, ni par l'autre parent, ni par notre
          équipe.
        </p>
        <p>
          C'est ce qui donne sa valeur à l'historique le jour où il faut s'y référer. C'est
          aussi ce qui change la façon d'écrire, et c'est voulu.
        </p>
      </>
    ),
  },
  {
    titre: 'Vous pouvez commencer seul',
    corps: (
      <>
        <p>
          L'autre parent n'a pas besoin d'être inscrit. Le calendrier, le journal et les
          documents fonctionnent dès maintenant ; la messagerie s'activera lorsqu'il aura
          rejoint le dossier.
        </p>
        <p>
          Un avocat, un médiateur ou un travailleur social peut aussi y être invité en
          lecture, si l'un de vous le décide.
        </p>
      </>
    ),
  },
];

const CLE_MEMOIRE = 'repere.bienvenue.vue';

/**
 * Présentation d'accueil, montrée une fois au parent qui n'a pas encore de dossier.
 *
 * Elle remplace la carte « aucun dossier » d'autrefois. Un écran vide qui décrit son
 * propre vide n'apprend rien : il fallait quatre lignes pour expliquer ce qu'est un
 * dossier, et elles ne tenaient pas dans une carte sans l'alourdir. La présentation prend
 * donc le premier plan, une idée par écran, puis s'efface — c'est la seule fois où elle
 * s'impose.
 *
 * Ce qu'elle annonce n'est pas décoratif : la symétrie des droits, l'absence d'accusé de
 * lecture et l'immuabilité sont les promesses du produit (docs/01 §5). Les découvrir en
 * cours de route donnerait le sentiment d'un piège ; annoncées d'emblée, elles sont un
 * cadre.
 *
 * Bâtie sur `<dialog>` et `showModal()` : le piège de focus, la fermeture par Échap et
 * l'inertie de l'arrière-plan viennent du navigateur, jamais d'une reconstitution.
 */
export function Bienvenue({
  ouverteParDefaut,
  onFermer,
}: {
  /** Vrai à la première venue. Faux quand le parent la rouvre lui-même. */
  ouverteParDefaut: boolean;
  onFermer: () => void;
}) {
  const dialogue = useRef<HTMLDialogElement>(null);
  const titre = useRef<HTMLHeadingElement>(null);
  const [etape, setEtape] = useState(0);

  useEffect(() => {
    const element = dialogue.current;
    if (!element || element.open) return;
    // `showModal` peut manquer (jsdom) : la présentation reste alors un bloc ordinaire
    // plutôt que de faire échouer le rendu.
    element.showModal?.();
  }, []);

  // À chaque écran, le focus revient sur le titre : sans cela, un lecteur d'écran
  // continuerait d'annoncer l'écran précédent pendant que le bouton « Suivant » garde le
  // focus, et le parent n'entendrait jamais le texte qu'il vient de demander.
  useEffect(() => {
    titre.current?.focus?.();
  }, [etape]);

  const derniere = etape === ETAPES.length - 1;
  const courante = ETAPES[etape]!;

  return (
    <dialog
      className="dialogue-bienvenue"
      ref={dialogue}
      aria-labelledby="bienvenue-titre"
      onClose={onFermer}
      onClick={(evenement) => {
        // Un clic qui atteint le `dialog` lui-même vient du voile ; le contenu est un
        // enfant et arrête l'événement avant.
        if (evenement.target === dialogue.current) dialogue.current?.close();
      }}
    >
      <div className="dialogue-bienvenue-contenu">
        <p className="dialogue-bienvenue-compteur" aria-hidden="true">
          {etape + 1} / {ETAPES.length}
        </p>

        <h2 id="bienvenue-titre" ref={titre} tabIndex={-1}>
          {courante.titre}
        </h2>
        <Invisible>
          Écran {etape + 1} sur {ETAPES.length}.
        </Invisible>

        <div className="dialogue-bienvenue-corps">{courante.corps}</div>

        <ol className="dialogue-bienvenue-jalons" aria-hidden="true">
          {ETAPES.map((pas, index) => (
            <li key={pas.titre} className={index === etape ? 'courant' : undefined} />
          ))}
        </ol>

        <div className="dialogue-bienvenue-actions">
          {etape > 0 ? (
            <Bouton variante="secondaire" onClick={() => setEtape(etape - 1)}>
              Retour
            </Bouton>
          ) : null}

          {derniere ? (
            <Link
              to="/dossiers/creer"
              className="bouton"
              onClick={() => dialogue.current?.close()}
            >
              Créer un dossier
            </Link>
          ) : (
            <Bouton onClick={() => setEtape(etape + 1)}>Suivant</Bouton>
          )}
        </div>

        <button
          type="button"
          className="dialogue-bienvenue-passer"
          onClick={() => dialogue.current?.close()}
        >
          {ouverteParDefaut && !derniere ? 'Passer la présentation' : 'Fermer'}
        </button>
      </div>
    </dialog>
  );
}

/**
 * Se souvient que la présentation a été vue, dans ce navigateur.
 *
 * `localStorage` peut être indisponible — navigation privée, stockage bloqué. On le
 * traite comme un confort : à défaut, la présentation reparaît, ce qui est un moindre mal
 * comparé à un écran qui ne s'affiche pas du tout.
 */
export const memoireBienvenue = {
  dejaVue(): boolean {
    try {
      return globalThis.localStorage?.getItem(CLE_MEMOIRE) === 'oui';
    } catch {
      return false;
    }
  },
  retenir(): void {
    try {
      globalThis.localStorage?.setItem(CLE_MEMOIRE, 'oui');
    } catch {
      // Sans mémoire, la présentation reparaîtra. Ce n'est pas une erreur à signaler.
    }
  },
};
